/**
 * Secondary actions on a record or an edit: report a problem, merge a duplicate into another
 * record, claim a publisher or creator. Each opens a short dialog and ends in an edit, a report
 * or a claim that people with the right role then act on.
 */
import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router'
import { BadgeCheck, Flag, GitMerge } from 'lucide-react'
import { Dialog } from './Dialog'
import { RecordPicker, type PickerItem } from '../forms/fields'
import { api, ApiError } from '../lib/api'
import { label, REPORT_REASON_LABELS } from '../lib/labels'
import { invalidate } from '../lib/query'
import { signInHref, useSession } from '../lib/session'
import { TYPE_LABELS } from '../lib/format'
import { REPORT_REASONS, type EntityType, type ReportReason } from '../../shared/model'

function useAuthed() {
  const session = useSession()
  const navigate = useNavigate()
  return (fn: () => void) => () => {
    if (!session.user) navigate(signInHref(window.location.pathname))
    else fn()
  }
}

export function ReportButton({ targetType, targetId }: { targetType: EntityType | 'edit'; targetId: string }) {
  const [open, setOpen] = useState(false)
  const [reason, setReason] = useState<ReportReason>('incorrect')
  const [details, setDetails] = useState('')
  const [state, setState] = useState<'idle' | 'busy' | 'done'>('idle')
  const [err, setErr] = useState('')
  const authed = useAuthed()
  const close = () => {
    setOpen(false)
    setState('idle')
    setErr('')
    setDetails('')
  }
  return (
    <>
      <button type="button" className="button button-quiet button-small" onClick={authed(() => setOpen(true))}>
        <Flag size={14} aria-hidden="true" /> Report
      </button>
      <Dialog open={open} onClose={close} title="Report a problem">
        {state === 'done' ? (
          <>
            <p>Thanks. A moderator will look at it; you can follow up in the record’s history.</p>
            <button type="button" className="button button-quiet" onClick={close}>
              Close
            </button>
          </>
        ) : (
          <form
            className="dialog-form"
            onSubmit={async (e) => {
              e.preventDefault()
              setState('busy')
              setErr('')
              try {
                await api('/api/v1/reports', {
                  method: 'POST',
                  json: { target_type: targetType, target_id: targetId, reason, details },
                })
                invalidate('/api/v1/stats')
                setState('done')
              } catch (x) {
                setErr(x instanceof ApiError ? x.message : 'Could not send the report')
                setState('idle')
              }
            }}
          >
            <fieldset className="field">
              <legend className="field-label">What is wrong?</legend>
              <div className="radio-list">
                {REPORT_REASONS.map((r) => (
                  <label key={r} className="radio">
                    <input type="radio" name="reason" value={r} checked={reason === r} onChange={() => setReason(r)} />
                    <span>{label(REPORT_REASON_LABELS, r)}</span>
                  </label>
                ))}
              </div>
            </fieldset>
            <label className="field">
              <span className="field-label">Details</span>
              <textarea
                rows={3}
                value={details}
                onChange={(e) => setDetails(e.target.value)}
                maxLength={2000}
                placeholder="What should it say instead, and where did you see that?"
              />
            </label>
            <p className="field-hint">To fix a mistake yourself, edit the record instead; edits are reviewed too.</p>
            {err && (
              <p className="field-error" role="alert">
                {err}
              </p>
            )}
            <div className="dialog-actions">
              <button type="button" className="button button-quiet" onClick={close}>
                Cancel
              </button>
              <button type="submit" className="button button-primary" disabled={state === 'busy'}>
                {state === 'busy' ? 'Sending…' : 'Send report'}
              </button>
            </div>
          </form>
        )}
      </Dialog>
    </>
  )
}

export function MergeButton({ type, id, name }: { type: EntityType; id: string; name: string }) {
  const session = useSession()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const [target, setTarget] = useState<PickerItem | null>(null)
  const [note, setNote] = useState('')
  const [err, setErr] = useState('')
  const [busy, setBusy] = useState(false)
  const authed = useAuthed()
  const moderator = session.can('moderator')
  // A moderator's merge applies at once. Refetch only once this record's page has gone: while the edit page
  // loads, the record page stays mounted, and fresh data there would follow `merged_into` to the target.
  const merged = useRef(false)
  useEffect(
    () => () => {
      if (merged.current) invalidate('/api/v1/')
    },
    [],
  )
  return (
    <>
      <button type="button" className="button button-quiet button-small" onClick={authed(() => setOpen(true))}>
        <GitMerge size={14} aria-hidden="true" /> {moderator ? 'Merge' : 'Propose merge'}
      </button>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        title={`Merge “${name}” into another ${TYPE_LABELS[type].one.toLowerCase()}`}
      >
        <form
          className="dialog-form"
          onSubmit={async (e) => {
            e.preventDefault()
            if (!target?.id) return
            setBusy(true)
            setErr('')
            try {
              const edit = await api<{ id: string; status: string }>('/api/v1/edits', {
                method: 'POST',
                json: { entity_type: type, entity_id: id, action: 'merge', into: target.id, note },
              })
              merged.current = true
              navigate(`/edits/${edit.id}`)
            } catch (x) {
              setErr(x instanceof ApiError ? x.message : 'Could not merge')
              setBusy(false)
            }
          }}
        >
          <p className="section-note">
            Everything that points at this record will point at the one you choose. Its name is kept as an alias, and
            empty fields there are filled from this one.{' '}
            {moderator ? '' : 'A moderator reviews merges before they happen.'}
          </p>
          {target ? (
            <div className="ref-item is-single">
              <span className="ref-name">{target.name}</span>
              <button type="button" className="button button-quiet button-small" onClick={() => setTarget(null)}>
                Change
              </button>
            </div>
          ) : (
            <label className="field">
              <span className="field-label">Merge into</span>
              <RecordPicker id="merge-target" type={type} exclude={[id]} onPick={setTarget} placeholder="Search…" />
            </label>
          )}
          <label className="field">
            <span className="field-label">Why are these the same?</span>
            <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} maxLength={2000} />
          </label>
          {err && (
            <p className="field-error" role="alert">
              {err}
            </p>
          )}
          <div className="dialog-actions">
            <button type="button" className="button button-quiet" onClick={() => setOpen(false)}>
              Cancel
            </button>
            <button type="submit" className="button button-primary" disabled={!target || busy}>
              {busy ? 'Submitting…' : moderator ? 'Merge' : 'Propose merge'}
            </button>
          </div>
        </form>
      </Dialog>
    </>
  )
}

export function ClaimButton({ type, id, name }: { type: 'publisher' | 'creator'; id: string; name: string }) {
  const [open, setOpen] = useState(false)
  const [evidence, setEvidence] = useState('')
  const [links, setLinks] = useState('')
  const [state, setState] = useState<'idle' | 'busy' | 'done'>('idle')
  const [err, setErr] = useState('')
  const authed = useAuthed()
  return (
    <>
      <button type="button" className="button button-quiet button-small" onClick={authed(() => setOpen(true))}>
        <BadgeCheck size={14} aria-hidden="true" /> {type === 'publisher' ? 'Claim this publisher' : 'Is this you?'}
      </button>
      <Dialog open={open} onClose={() => setOpen(false)} title={`Claim ${name}`}>
        {state === 'done' ? (
          <>
            <p>
              Thanks. An admin will check your evidence and may contact you. You can see the claim on your account page.
            </p>
            <button type="button" className="button button-quiet" onClick={() => setOpen(false)}>
              Close
            </button>
          </>
        ) : (
          <form
            className="dialog-form"
            onSubmit={async (e) => {
              e.preventDefault()
              setState('busy')
              setErr('')
              try {
                await api('/api/v1/claims', {
                  method: 'POST',
                  json: {
                    entity_type: type,
                    entity_id: id,
                    evidence,
                    links: links
                      .split(/\s+/)
                      .map((l) => l.trim())
                      .filter(Boolean),
                  },
                })
                setState('done')
              } catch (x) {
                setErr(x instanceof ApiError ? x.message : 'Could not send the claim')
                setState('idle')
              }
            }}
          >
            <p className="section-note">
              Verified {type === 'publisher' ? 'publishers' : 'creators'} can update their own records without waiting
              for review, and review other people’s changes to them. An admin checks every claim by hand.
            </p>
            <label className="field">
              <span className="field-label">Who are you, and how can we confirm it?</span>
              <textarea
                rows={4}
                value={evidence}
                onChange={(e) => setEvidence(e.target.value)}
                minLength={20}
                maxLength={4000}
                required
                placeholder="For example: I run the company; you can email me at the address on our website, or see the link to this account on our social profile."
              />
            </label>
            <label className="field">
              <span className="field-label">Links that show it</span>
              <textarea
                rows={2}
                value={links}
                onChange={(e) => setLinks(e.target.value)}
                placeholder="Your website, store page or social profile, one per line…"
              />
            </label>
            {err && (
              <p className="field-error" role="alert">
                {err}
              </p>
            )}
            <div className="dialog-actions">
              <button type="button" className="button button-quiet" onClick={() => setOpen(false)}>
                Cancel
              </button>
              <button
                type="submit"
                className="button button-primary"
                disabled={state === 'busy' || evidence.trim().length < 20}
              >
                {state === 'busy' ? 'Sending…' : 'Send claim'}
              </button>
            </div>
          </form>
        )}
      </Dialog>
    </>
  )
}

/** The small "verified" mark beside a claimed publisher or creator. */
export function VerifiedBadge() {
  return (
    <span className="verified" title="Verified by an admin">
      <BadgeCheck size={16} aria-hidden="true" /> Verified
    </span>
  )
}
